// /admin/integraciones — Health check del CRM (tokens Meta + webhooks + último mensaje)
// Llamadas: edge function `crm-health` (consolidado) + RPC `crm_ig_refresh_token` (refresh manual)
// Para ver de un vistazo si el CRM va a fallar en las próximas horas / días.

import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import CanalLogo from '../../components/CanalLogo'
import { supabase } from '../../lib/supabase'

export default function Integraciones() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refrescando, setRefrescando] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState(null)

  async function cargar() {
    setLoading(true); setError(null)
    try {
      const { data, error } = await supabase.functions.invoke('crm-health', { body: {} })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
      setData(data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { cargar() }, [])

  async function refrescarIG() {
    if (refrescando) return
    setRefrescando(true); setRefreshMsg(null)
    try {
      const { data, error } = await supabase.rpc('crm_ig_refresh_token')
      if (error) throw new Error(error.message)
      if (data?.ok === false) throw new Error(JSON.stringify(data))
      setRefreshMsg({ tipo: 'ok', txt: `✅ Token IG refrescado: vence ${data?.vence || '60 días más'}` })
      await cargar()
    } catch (e) {
      setRefreshMsg({ tipo: 'error', txt: 'Error: ' + e.message })
    } finally {
      setRefrescando(false)
    }
  }

  function fmtTiempo(ts) {
    if (!ts) return '—'
    const d = new Date(ts)
    return d.toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  function colorEstado(ok) { return ok ? '#16A34A' : '#DC2626' }
  function bgEstado(ok) { return ok ? '#DCFCE7' : '#FEE2E2' }

  return (
    <AdminLayout title="Integraciones">
      <style>{CSS}</style>
      <div className="int-wrap">
        <div className="int-head">
          <div>
            <h1>Integraciones CRM</h1>
            <p>Estado de tokens Meta, webhooks y actividad de los canales. Se actualiza cuando refrescás esta pantalla.</p>
          </div>
          <button className="int-btn-refresh" onClick={cargar} disabled={loading}>
            {loading ? '⏳ Cargando…' : '🔄 Actualizar'}
          </button>
        </div>

        {error && <div className="int-banner-error">❌ {error}</div>}

        {data && (
          <>
            <div className="int-cards">
              <CanalCard
                canal="whatsapp"
                titulo="WhatsApp Cloud API"
                d={data.whatsapp}
                fmtTiempo={fmtTiempo}
                colorEstado={colorEstado}
                bgEstado={bgEstado}
              />
              <CanalCard
                canal="instagram"
                titulo="Instagram Business"
                d={data.instagram}
                fmtTiempo={fmtTiempo}
                colorEstado={colorEstado}
                bgEstado={bgEstado}
              />
            </div>

            <div className="int-actions">
              <button className="int-btn-action" onClick={refrescarIG} disabled={refrescando}>
                {refrescando ? '⏳ Refrescando…' : '🔄 Refrescar token IG ahora'}
              </button>
              <a
                className="int-btn-link"
                href="https://supabase.com/dashboard/project/xsviveqhkppmhncodhuv/functions"
                target="_blank"
                rel="noopener noreferrer"
              >
                ⚙️ Dashboard edge functions
              </a>
              <a
                className="int-btn-link"
                href="https://developers.facebook.com/apps/1490024032813462/instagram-business/api-setup/"
                target="_blank"
                rel="noopener noreferrer"
              >
                📱 Meta panel IG
              </a>
            </div>

            {refreshMsg && (
              <div className={`int-banner int-banner-${refreshMsg.tipo}`}>{refreshMsg.txt}</div>
            )}

            {/* Log de refreshes */}
            <h2 className="int-h2">📋 Últimos refreshes de token</h2>
            {(data.refresh_log || []).length === 0 ? (
              <div className="int-empty">Sin refreshes registrados todavía. El cron diario (04:00 UY) va a empezar cuando el token tenga ≤10 días para vencer.</div>
            ) : (
              <table className="int-tabla">
                <thead>
                  <tr><th>Fecha</th><th>Canal</th><th>HTTP</th><th>Vence en (días)</th><th>Error</th></tr>
                </thead>
                <tbody>
                  {data.refresh_log.map((r, i) => (
                    <tr key={i}>
                      <td>{fmtTiempo(r.ejecutado_at)}</td>
                      <td><CanalLogo canal={r.canal} size={14} /> {r.canal}</td>
                      <td><span className={`int-pill ${r.http_status === 200 ? 'int-pill-ok' : 'int-pill-err'}`}>{r.http_status}</span></td>
                      <td>{r.vence_en_dias || '—'}</td>
                      <td className="int-muted">{r.error_detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Log auto-reply */}
            <h2 className="int-h2">💬 Últimas auto-respuestas fuera de horario</h2>
            {(data.auto_reply_log || []).length === 0 ? (
              <div className="int-empty">Sin auto-respuestas registradas.</div>
            ) : (
              <table className="int-tabla">
                <thead>
                  <tr><th>Fecha</th><th>Tipo</th></tr>
                </thead>
                <tbody>
                  {data.auto_reply_log.map((r, i) => (
                    <tr key={i}>
                      <td>{fmtTiempo(r.created_at)}</td>
                      <td>{r.tipo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="int-foot">
              Generado: {fmtTiempo(data.generado_at)}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  )
}

function CanalCard({ canal, titulo, d, fmtTiempo, colorEstado, bgEstado }) {
  if (!d) return null
  const tokenOK = d.token?.valido
  const hookOK = d.webhook?.ok
  const verde = tokenOK && hookOK
  const amarillo = (tokenOK && !hookOK) || (!tokenOK && hookOK)
  const rojo = !tokenOK && !hookOK
  const semaforo = verde ? '🟢' : amarillo ? '🟡' : '🔴'

  return (
    <div className="int-card">
      <div className="int-card-head">
        <CanalLogo canal={canal} size={28} />
        <div style={{ flex: 1 }}>
          <h3>{titulo}</h3>
          <p className="int-muted">{d.token?.detalle}</p>
        </div>
        <div className="int-semaforo">{semaforo}</div>
      </div>

      <div className="int-card-rows">
        <div className="int-row">
          <span className="int-row-label">🔑 Token</span>
          <span className="int-row-val">
            <span style={{ color: colorEstado(tokenOK), fontWeight: 700 }}>
              {tokenOK ? '✅ Válido' : '❌ Inválido'}
            </span>
            {d.token?.dias_desde_update != null && (
              <span className="int-muted"> · cargado hace {d.token.dias_desde_update}d</span>
            )}
          </span>
        </div>

        <div className="int-row">
          <span className="int-row-label">🌐 Webhook</span>
          <span className="int-row-val">
            <span style={{ color: colorEstado(hookOK), fontWeight: 700 }}>
              {hookOK ? '✅ Activo' : '❌ Roto'}
            </span>
            <span className="int-muted"> · HTTP {d.webhook?.status}</span>
          </span>
        </div>
        {!hookOK && (
          <div className="int-warn">
            <strong>⚠ {d.webhook?.detalle}</strong>
            {d.webhook?.status === 401 && (
              <div style={{ marginTop: 6 }}>
                Andá a:{' '}
                <a
                  href={`https://supabase.com/dashboard/project/xsviveqhkppmhncodhuv/functions/${canal === 'instagram' ? 'ig-webhook' : 'wa-webhook'}/details`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1A3461', fontWeight: 700 }}
                >
                  Dashboard {canal === 'instagram' ? 'ig-webhook' : 'wa-webhook'}
                </a>
                {' '}→ toggle <strong>Enforce JWT verification</strong> a OFF.
              </div>
            )}
          </div>
        )}

        <div className="int-row">
          <span className="int-row-label">📥 Último in</span>
          <span className="int-row-val">
            {fmtTiempo(d.ultimo_in)}
            {d.horas_desde_ultimo_in != null && (
              <span className="int-muted"> · hace {d.horas_desde_ultimo_in}h</span>
            )}
          </span>
        </div>

        <div className="int-row">
          <span className="int-row-label">📤 Último out</span>
          <span className="int-row-val">{fmtTiempo(d.ultimo_out)}</span>
        </div>
      </div>
    </div>
  )
}

const CSS = `
.int-wrap { padding: 20px 28px 40px; max-width: 1200px; margin: 0 auto; font-family: 'Inter', -apple-system, sans-serif; color: #1A3461; }
.int-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 22px; flex-wrap: wrap; }
.int-head h1 { font-size: 24px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 4px; }
.int-head p { font-size: 13px; color: #64748B; margin: 0; max-width: 540px; line-height: 1.5; }
.int-btn-refresh {
  background: linear-gradient(135deg, #1A3461, #00A8B5);
  color: #fff; border: 0; padding: 10px 18px;
  border-radius: 10px; font-weight: 700; font-size: 13px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 2px 8px rgba(26, 52, 97, 0.18);
}
.int-btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }
.int-banner-error { background: #FEE2E2; color: #991B1B; padding: 14px 18px; border-radius: 10px; font-weight: 600; margin-bottom: 20px; }
.int-banner { padding: 12px 16px; border-radius: 10px; margin: 14px 0; font-weight: 600; font-size: 13px; }
.int-banner-ok { background: #DCFCE7; color: #14532D; }
.int-banner-error { background: #FEE2E2; color: #991B1B; }

.int-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 22px; }
@media (max-width: 768px) { .int-cards { grid-template-columns: 1fr; } }

.int-card {
  background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; padding: 20px;
  box-shadow: 0 1px 3px rgba(15, 27, 54, 0.04);
}
.int-card-head { display: flex; gap: 14px; align-items: center; margin-bottom: 16px; }
.int-card-head h3 { font-size: 15px; font-weight: 800; margin: 0; letter-spacing: -0.01em; }
.int-card-head p { font-size: 12px; margin: 2px 0 0; }
.int-semaforo { font-size: 22px; }

.int-card-rows { display: flex; flex-direction: column; gap: 9px; }
.int-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-size: 13px; padding: 6px 0; }
.int-row-label { color: #64748B; font-weight: 600; flex-shrink: 0; }
.int-row-val { color: #1A3461; text-align: right; }
.int-muted { color: #94A3B8; font-weight: 400; }

.int-warn {
  background: #FEF3C7; color: #78350F; padding: 10px 12px;
  border-radius: 8px; font-size: 12px; line-height: 1.5;
  margin: 4px 0;
}

.int-actions { display: flex; gap: 10px; margin: 18px 0; flex-wrap: wrap; }
.int-btn-action {
  background: #1A3461; color: #fff; border: 0;
  padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
}
.int-btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
.int-btn-link {
  background: #fff; border: 1px solid #E2E8F0; color: #1A3461;
  padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700;
  text-decoration: none;
}
.int-btn-link:hover { border-color: #00A8B5; color: #00A8B5; }

.int-h2 { font-size: 14px; font-weight: 700; margin: 26px 0 12px; letter-spacing: -0.01em; color: #1A3461; }
.int-empty { color: #94A3B8; font-size: 13px; padding: 18px; background: #F8FAFC; border-radius: 10px; }

.int-tabla {
  width: 100%; border-collapse: collapse; background: #fff;
  border-radius: 10px; overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 27, 54, 0.04);
  border: 1px solid #E2E8F0;
}
.int-tabla th, .int-tabla td {
  padding: 10px 14px; text-align: left; font-size: 12.5px;
  border-bottom: 1px solid #F1F5F9;
}
.int-tabla th { background: #F8FAFC; font-weight: 700; color: #64748B; }
.int-tabla tbody tr:last-child td { border-bottom: 0; }

.int-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.int-pill-ok { background: #DCFCE7; color: #14532D; }
.int-pill-err { background: #FEE2E2; color: #991B1B; }

.int-foot { margin-top: 24px; font-size: 11px; color: #94A3B8; text-align: right; }
`
